// Редактор шаблона: название, упражнения по порядку, целевые подходы, отдых, прогрессия.
// Упражнение — как на тренировке: группа мышц, сворачивается по заголовку; в раскрытом —
// порядок стрелками, «Заменить» на месте, «Техника», убрать (ExerciseActions).
// «С ИИ» — правка словами: ИИ предлагает правки, «Применить» переносит их в форму.

import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, Icon, IconButton, Input, PageSpinner, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { AiEditSheet } from '@/pages/ai/EditChat'
import { ExerciseActions } from '@/pages/exercises/ExerciseActions'
import { ExercisePicker } from '@/pages/exercises/ExercisePicker'
import {
  trainingApi,
  useExerciseMuscle,
  useInvalidateTraining,
  useTemplate,
  type Exercise,
  type Template,
} from '@/pages/workouts/api'
import { setFields } from '@/pages/workouts/logic'

import {
  applyEdit,
  emptyRow,
  formFromTemplate,
  formToInput,
  itemSummary,
  moveItem,
  newItem,
  replaceItem,
  toEditItems,
  type TemplateForm,
  type TemplateItem,
} from './logic'

const cellBase = 'h-10 min-w-0 rounded-md border border-border bg-surface px-2 text-sm tabular-nums text-fg focus:outline-2 focus:outline-accent'
const cell = `${cellBase} w-full`

function ItemEditor({
  item,
  index,
  total,
  open,
  onToggle,
  onChange,
  onMove,
  onReplace,
  onRemove,
}: {
  item: TemplateItem
  index: number
  total: number
  open: boolean
  onToggle: () => void
  onChange: (item: TemplateItem) => void
  onMove: (delta: -1 | 1) => void
  onReplace: (exercise: Exercise) => void
  onRemove: () => void
}) {
  const fields = setFields(item.tracking)
  const columns = [
    fields.weight ? { key: 'weight' as const, label: item.tracking === 'bodyweight_reps' ? 'Доп. вес, кг' : 'Вес, кг' } : null,
    fields.reps ? { key: 'reps' as const, label: 'Повторы' } : null,
    fields.duration ? { key: 'duration' as const, label: 'Время, м:сс' } : null,
    fields.distance ? { key: 'distance' as const, label: 'Дистанция, м' } : null,
    { key: 'rest' as const, label: 'Отдых, с' },
  ].filter((c) => c !== null)
  const setRow = (rowIndex: number, key: (typeof columns)[number]['key'], value: string) =>
    onChange({ ...item, rows: item.rows.map((r, i) => (i === rowIndex ? { ...r, [key]: value } : r)) })
  const muscle = useExerciseMuscle(item.exercise_id)
  const bodyId = `template-item-${item.key}`
  // Свёрнуто — под названием ещё и подходы, чтобы шаблон читался без раскрытия.
  const subtitle = [muscle, open ? null : itemSummary(item)].filter(Boolean).join(' · ')

  return (
    <section aria-label={item.name} className="min-w-0 rounded-xl border border-border bg-surface">
      <div className="flex items-center py-1 pl-3 pr-2">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          onClick={onToggle}
          className="flex min-h-14 min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        >
          <span className="shrink-0 self-start text-sm tabular-nums leading-snug text-muted">{index + 1}.</span>
          <span className="flex min-w-0 flex-1 flex-col">
            <h2 className="line-clamp-2 text-sm font-semibold leading-snug text-fg">
              {item.name}
              {item.archived ? (
                <Badge tone="warning" className="ml-2 align-middle">
                  в архиве
                </Badge>
              ) : null}
            </h2>
            {subtitle ? <span className="truncate text-xs text-muted tabular-nums">{subtitle}</span> : null}
          </span>
          <Icon
            name="chevron-down"
            size={20}
            className={cn('mx-1 shrink-0 text-muted transition-transform', open && 'rotate-180')}
          />
        </button>
      </div>
      {open ? (
        <div id={bodyId} className="border-t border-border px-4 pb-4 pt-2">
          <ExerciseActions
            exerciseId={item.exercise_id}
            name={item.name}
            index={index}
            total={total}
            onMove={onMove}
            onReplace={onReplace}
            onRemove={onRemove}
            className="-mx-2 mb-2"
          />
          {/* table-fixed: колонки подхода одной ширины, иначе «Вес» сжат под короткий заголовок. */}
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="w-7 pb-1 font-medium">#</th>
                {columns.map((c) => (
                  <th key={c.key} className="pb-1 pr-2 font-medium">
                    {c.label}
                  </th>
                ))}
                <th className="w-11" />
              </tr>
            </thead>
            <tbody>
              {item.rows.map((row, rowIndex) => (
                <tr key={row.key}>
                  <td className="py-1 tabular-nums text-muted">{rowIndex + 1}</td>
                  {columns.map((c) => (
                    <td key={c.key} className="py-1 pr-2">
                      <input
                        className={cell}
                        inputMode="decimal"
                        aria-label={`${c.label}, подход ${rowIndex + 1}`}
                        value={row[c.key]}
                        onChange={(e) => setRow(rowIndex, c.key, e.target.value)}
                      />
                    </td>
                  ))}
                  <td className="py-1">
                    <IconButton
                      icon="minus"
                      label="Убрать подход"
                      onClick={() => onChange({ ...item, rows: item.rows.filter((_, i) => i !== rowIndex) })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button size="sm" variant="ghost" icon="plus" onClick={() => onChange({ ...item, rows: [...item.rows, emptyRow(item.rows.at(-1))] })}>
              Подход
            </Button>
            {fields.weight ? (
              <label className="flex items-center gap-2 whitespace-nowrap text-sm text-fg">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={item.progression}
                  onChange={(e) => onChange({ ...item, progression: e.target.checked })}
                />
                Прогрессия, шаг
                <input
                  className={cn(cellBase, 'w-16')}
                  inputMode="decimal"
                  aria-label="Шаг прогрессии, кг"
                  value={item.increment}
                  disabled={!item.progression}
                  onChange={(e) => onChange({ ...item, increment: e.target.value })}
                />
                кг
              </label>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}

function Editor({ template }: { template: Template | null }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateTraining()
  const [form, setForm] = useState<TemplateForm>(() => formFromTemplate(template))
  // Раскрытые упражнения: у сохранённого шаблона всё свёрнуто, только что добавленное — раскрыто.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())
  const [picking, setPicking] = useState(false)
  const [ai, setAi] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (input: NonNullable<ReturnType<typeof formToInput>['input']>) =>
      template ? trainingApi.updateTemplate(template.id, input) : trainingApi.createTemplate(input),
    onSuccess: async (saved) => {
      await invalidate()
      toast.show({ message: 'Шаблон сохранён', tone: 'accent' })
      if (!template) navigate(`/templates/${saved.id}`, { replace: true })
    },
  })
  const submit = () => {
    const { input, error: problem } = formToInput(form)
    setError(problem)
    // Ошибка в числах — раскрыть всё, чтобы было видно, где она.
    if (problem) setOpen(new Set(form.items.map((it) => it.key)))
    if (input) save.mutate(input)
  }
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const allOpen = form.items.length > 0 && form.items.every((it) => open.has(it.key))
  // Правки ИИ — в форму (сохранит «Сохранить»); новые упражнения раскрыты, как добавленные руками.
  const applyAi = (operations: Parameters<typeof applyEdit>[1]) => {
    const before = new Set(form.items.map((it) => it.key))
    const items = applyEdit(form.items, operations)
    setForm((f) => ({ ...f, items }))
    setOpen((prev) => new Set([...prev, ...items.filter((it) => !before.has(it.key)).map((it) => it.key)]))
  }
  const setItem = (index: number, item: TemplateItem) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === index ? item : it)) }))

  return (
    <>
      <PageHeader
        title={template ? template.name : 'Новый шаблон'}
        back={{ to: '/templates', label: 'Шаблоны' }}
        actions={
          <>
            <Button icon="ai" onClick={() => setAi(true)}>
              С ИИ
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={submit} className="max-lg:hidden">
              Сохранить
            </Button>
          </>
        }
      />
      <div className="grid gap-4">
        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Название" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Верх А" />
            <Input label="Заметка" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </Card>
        {form.items.length > 1 ? (
          <div className="-mb-2 flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpen(allOpen ? new Set() : new Set(form.items.map((it) => it.key)))}
            >
              {allOpen ? 'Свернуть все' : 'Развернуть все'}
            </Button>
          </div>
        ) : null}
        {form.items.length === 0 ? (
          <Card>
            <EmptyState icon="list" title="Добавь упражнения" description="Порядок поменяешь стрелками в раскрытом упражнении." />
          </Card>
        ) : (
          form.items.map((item, index) => (
            <ItemEditor
              key={item.key}
              item={item}
              index={index}
              total={form.items.length}
              open={open.has(item.key)}
              onToggle={() => toggle(item.key)}
              onChange={(next) => setItem(index, next)}
              onMove={(delta) => setForm((f) => ({ ...f, items: moveItem(f.items, index, delta) }))}
              onReplace={(ex) => setItem(index, replaceItem(item, ex))}
              onRemove={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== index) }))}
            />
          ))
        )}
        <div className="flex flex-wrap gap-2">
          <Button icon="plus" onClick={() => setPicking(true)}>
            Добавить упражнение
          </Button>
          <Button icon="ai" variant="ghost" onClick={() => setAi(true)}>
            С ИИ
          </Button>
        </div>
        <ErrorText>{error}</ErrorText>
        <ErrorText error={save.error} />
        {/* На телефоне «Сохранить» всегда под рукой — прилипает к низу над меню. */}
        <div className="sticky bottom-(--bottom-ui) z-20 -mx-4 border-t border-border bg-bg/95 px-4 py-2 backdrop-blur lg:hidden">
          <Button variant="primary" block loading={save.isPending} onClick={submit}>
            Сохранить
          </Button>
        </div>
      </div>
      <AiEditSheet
        open={ai}
        onClose={() => setAi(false)}
        mode="template"
        title={form.name.trim() || null}
        items={toEditItems(form.items)}
        onApply={applyAi}
      />
      {picking ? (
        <ExercisePicker
          open
          onClose={() => setPicking(false)}
          onPick={(ex) => {
            const item = newItem(ex)
            setForm((f) => ({ ...f, items: [...f.items, item] }))
            setOpen((prev) => new Set(prev).add(item.key))
          }}
        />
      ) : null}
    </>
  )
}

export function TemplateEditPage() {
  const { id = 'new' } = useParams()
  const isNew = id === 'new'
  const template = useTemplate(isNew ? null : id)
  if (isNew) return <Editor template={null} />
  if (template.isPending) return <PageSpinner />
  if (template.error) return <ErrorText error={template.error} />
  return <Editor key={template.data.id} template={template.data} />
}
